/** biome-ignore-all lint/suspicious/noArrayIndexKey: editor rows use index keys before persistence */
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import type {
  RepeaterCountMode,
  RepeaterItemFieldDef,
  RepeaterSettings,
  RepeaterSubFieldType,
} from "@/server/platform/db/schema/forms";
import {
  repeaterCountModes,
  repeaterSubFieldTypes,
} from "@/server/platform/db/schema/forms";

type RepeaterFieldSettingsEditorProps = {
  value: RepeaterSettings;
  onChange: (next: RepeaterSettings) => void;
  disabled?: boolean;
};

function slugFromLabel(label: string, fallback: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || fallback;
}

export function RepeaterFieldSettingsEditor({
  value,
  onChange,
  disabled,
}: RepeaterFieldSettingsEditorProps) {
  const patch = (partial: Partial<RepeaterSettings>) =>
    onChange({ ...value, ...partial });

  const patchItemField = <K extends keyof RepeaterItemFieldDef>(
    idx: number,
    key: K,
    val: RepeaterItemFieldDef[K],
  ) => {
    const next = value.itemFields.map((row, i) =>
      i === idx ? { ...row, [key]: val } : row,
    );
    patch({ itemFields: next });
  };

  const patchItemOption = (
    fieldIdx: number,
    optIdx: number,
    key: "value" | "label" | "enabled",
    val: string | boolean,
  ) => {
    const field = value.itemFields[fieldIdx];
    if (!field) return;
    const options = [...(field.options ?? [])];
    const current = options[optIdx] ?? { value: "", label: "" };
    options[optIdx] = {
      ...current,
      [key]: val,
    } as (typeof options)[number];
    patchItemField(fieldIdx, "options", options);
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border p-3">
      <div className="flex flex-col gap-2">
        <Label>ໂໝດຈຳນວນລາຍການ</Label>
        <Select
          value={value.countMode}
          disabled={disabled}
          onValueChange={(v) => {
            const mode = v as RepeaterCountMode;
            if (mode === "fixed") {
              patch({
                countMode: mode,
                fixedCount: value.fixedCount ?? 1,
                allowAddRemove: false,
              });
              return;
            }
            patch({
              countMode: mode,
              minItems: value.minItems ?? 1,
              maxItems: value.maxItems ?? 10,
              allowAddRemove: true,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {repeaterCountModes.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {mode === "fixed"
                  ? "ຄົງທີ່ (N ລາຍການ)"
                  : mode === "range"
                    ? "ຊ່ວງ (min–max)"
                    : "ປັບໄດ້ (submitter ເພີ່ມ/ລຶບ)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value.countMode === "fixed" ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="repeater-fixed-count">ຈຳນວນລາຍການ</Label>
          <Input
            id="repeater-fixed-count"
            type="number"
            min={1}
            max={100}
            disabled={disabled}
            value={value.fixedCount ?? 1}
            onChange={(e) =>
              patch({
                fixedCount: Math.max(
                  1,
                  Number.parseInt(e.target.value, 10) || 1,
                ),
              })
            }
          />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="repeater-min-items">ຢ່າງໜ້ອຍ</Label>
            <Input
              id="repeater-min-items"
              type="number"
              min={0}
              max={100}
              disabled={disabled}
              value={value.minItems ?? 0}
              onChange={(e) =>
                patch({
                  minItems: Math.max(
                    0,
                    Number.parseInt(e.target.value, 10) || 0,
                  ),
                })
              }
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="repeater-max-items">ສູງສຸດ</Label>
            <Input
              id="repeater-max-items"
              type="number"
              min={1}
              max={100}
              disabled={disabled}
              value={value.maxItems ?? 10}
              onChange={(e) =>
                patch({
                  maxItems: Math.max(
                    1,
                    Number.parseInt(e.target.value, 10) || 1,
                  ),
                })
              }
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="repeater-item-label">ປ້າຍແຕ່ລະລາຍການ</Label>
        <Input
          id="repeater-item-label"
          disabled={disabled}
          placeholder="ລາຍການ #{index}"
          value={value.itemLabelTemplate ?? ""}
          onChange={(e) => patch({ itemLabelTemplate: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          ໃຊ້ {"#{index}"} ສຳລັບເລກລຳດັບ
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="repeater-show-number"
            checked={value.showItemNumber !== false}
            disabled={disabled}
            onCheckedChange={(v) => patch({ showItemNumber: Boolean(v) })}
          />
          <Label htmlFor="repeater-show-number" className="font-normal">
            ສະແດງເລກລຳດັບ
          </Label>
        </div>
        {value.countMode !== "fixed" ? (
          <div className="flex items-center gap-2">
            <Checkbox
              id="repeater-allow-add"
              checked={value.allowAddRemove !== false}
              disabled={disabled}
              onCheckedChange={(v) => patch({ allowAddRemove: Boolean(v) })}
            />
            <Label htmlFor="repeater-allow-add" className="font-normal">
              ອະນຸຍາດເພີ່ມ/ລຶບລາຍການ
            </Label>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Label>ຟິວໃນແຕ່ລະລາຍການ</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              patch({
                itemFields: [
                  ...value.itemFields,
                  {
                    key: `field_${Date.now().toString(36)}`,
                    label: `ຟິວ ${value.itemFields.length + 1}`,
                    type: "text",
                  },
                ],
              })
            }
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            ເພີ່ມຟິວ
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {value.itemFields.map((field, idx) => (
            <div
              key={idx}
              className="flex flex-col gap-3 rounded-md border border-dashed p-3"
            >
              <div className="flex flex-wrap items-start gap-2">
                <Input
                  className="min-w-[8rem] flex-1"
                  placeholder="ປ້າຍຟິວ"
                  disabled={disabled}
                  value={field.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    const autoKey = slugFromLabel(
                      label,
                      field.key || `field_${idx + 1}`,
                    );
                    patchItemField(idx, "label", label);
                    if (
                      field.key.startsWith("field_") ||
                      field.key === slugFromLabel(field.label, "")
                    ) {
                      patchItemField(idx, "key", autoKey);
                    }
                  }}
                />
                <Input
                  className="min-w-[6rem] flex-1"
                  placeholder="key"
                  disabled={disabled}
                  value={field.key}
                  onChange={(e) => patchItemField(idx, "key", e.target.value)}
                />
                <Select
                  value={field.type}
                  disabled={disabled}
                  onValueChange={(v) =>
                    patchItemField(idx, "type", v as RepeaterSubFieldType)
                  }
                >
                  <SelectTrigger className="w-[8rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {repeaterSubFieldTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled || value.itemFields.length <= 1}
                  aria-label="ລຶບຟິວ"
                  onClick={() =>
                    patch({
                      itemFields: value.itemFields.filter((_, i) => i !== idx),
                    })
                  }
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id={`repeater-item-req-${idx}`}
                  checked={field.required === true}
                  disabled={disabled}
                  onCheckedChange={(v) =>
                    patchItemField(idx, "required", Boolean(v))
                  }
                />
                <Label
                  htmlFor={`repeater-item-req-${idx}`}
                  className="font-normal text-xs"
                >
                  ຈຳເປັນ
                </Label>
              </div>

              {field.type === "text" ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="pattern (regex, ບໍ່ບັງຄັບ)"
                    disabled={disabled}
                    value={field.pattern ?? ""}
                    onChange={(e) =>
                      patchItemField(idx, "pattern", e.target.value)
                    }
                  />
                  <Input
                    placeholder="placeholder"
                    disabled={disabled}
                    value={field.placeholder ?? ""}
                    onChange={(e) =>
                      patchItemField(idx, "placeholder", e.target.value)
                    }
                  />
                </div>
              ) : null}

              {field.type === "number" ? (
                <div className="grid gap-2 sm:grid-cols-3">
                  <Input
                    type="number"
                    placeholder="min"
                    disabled={disabled}
                    value={field.min ?? ""}
                    onChange={(e) => {
                      const t = e.target.value.trim();
                      patchItemField(
                        idx,
                        "min",
                        t.length ? Number(t) : undefined,
                      );
                    }}
                  />
                  <Input
                    type="number"
                    placeholder="max"
                    disabled={disabled}
                    value={field.max ?? ""}
                    onChange={(e) => {
                      const t = e.target.value.trim();
                      patchItemField(
                        idx,
                        "max",
                        t.length ? Number(t) : undefined,
                      );
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`repeater-item-int-${idx}`}
                      checked={field.integerOnly === true}
                      disabled={disabled}
                      onCheckedChange={(v) =>
                        patchItemField(idx, "integerOnly", Boolean(v))
                      }
                    />
                    <Label
                      htmlFor={`repeater-item-int-${idx}`}
                      className="font-normal text-xs"
                    >
                      ເຕັມເທົ່ານັ້ນ
                    </Label>
                  </div>
                </div>
              ) : null}

              {field.type === "choice" ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">ທາງເລືອກ</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={disabled}
                      onClick={() =>
                        patchItemField(idx, "options", [
                          ...(field.options ?? []),
                          {
                            value: `v_${Date.now().toString(36)}`,
                            label: `ທາງເລືອກ ${(field.options?.length ?? 0) + 1}`,
                            enabled: true,
                          },
                        ])
                      }
                    >
                      <PlusIcon className="mr-1 h-3 w-3" />
                      ເພີ່ມ
                    </Button>
                  </div>
                  {(field.options ?? []).map((opt, optIdx) => (
                    <div key={optIdx} className="flex flex-wrap gap-2">
                      <Input
                        className="min-w-[5rem] flex-1"
                        placeholder="value"
                        disabled={disabled}
                        value={opt.value}
                        onChange={(e) =>
                          patchItemOption(idx, optIdx, "value", e.target.value)
                        }
                      />
                      <Input
                        className="min-w-[5rem] flex-1"
                        placeholder="ປ້າຍ"
                        disabled={disabled}
                        value={opt.label}
                        onChange={(e) =>
                          patchItemOption(idx, optIdx, "label", e.target.value)
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={disabled}
                        aria-label="ລຶບທາງເລືອກ"
                        onClick={() =>
                          patchItemField(
                            idx,
                            "options",
                            (field.options ?? []).filter((_, i) => i !== optIdx),
                          )
                        }
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
